import React, { useEffect, useRef, useState } from "react";

export default function DirectionsMap({
    originPlaceId,
    destinationPlaceId,
    nextStep,
    prevStep,
    setTotalDistance,
    setTotalFare,
    ratePerKm = 14,
}) {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const [directionsResults, setDirectionsResults] = useState(null);
    const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
    const polylinesRef = useRef([]);

    // Initialize map and fetch directions with alternatives
    useEffect(() => {
        if (!window.google) return;

        if (!mapInstance.current) {
            mapInstance.current = new window.google.maps.Map(mapRef.current, {
                zoom: 6,
                center: { lat: 10.790483, lng: 78.704673 }, // Example center
            });
        }

        if (originPlaceId && destinationPlaceId) {
            const directionsService = new window.google.maps.DirectionsService();

            directionsService.route(
                {
                    origin: { placeId: originPlaceId },
                    destination: { placeId: destinationPlaceId },
                    travelMode: window.google.maps.TravelMode.DRIVING,
                    provideRouteAlternatives: true,
                },
                (result, status) => {
                    if (status === window.google.maps.DirectionsStatus.OK) {
                        setDirectionsResults(result);
                        setSelectedRouteIndex(0);
                    } else {
                        console.error("Error fetching directions: " + status);
                    }
                }
            );
        }
    }, [originPlaceId, destinationPlaceId]);

    // Draw polylines and highlight selected route
    useEffect(() => {
        if (!directionsResults || !mapInstance.current) return;

        polylinesRef.current.forEach((poly) => poly.setMap(null));
        polylinesRef.current = [];

        directionsResults.routes.forEach((route, i) => {
            const polyline = new window.google.maps.Polyline({
                path: route.overview_path,
                strokeColor: i === selectedRouteIndex ? "#1976d2" : "#9e9e9e",
                strokeOpacity: i === selectedRouteIndex ? 0.9 : 0.5,
                strokeWeight: i === selectedRouteIndex ? 6 : 4,
                map: mapInstance.current,
                clickable: true,
            });

            polyline.addListener("click", () => {
                setSelectedRouteIndex(i);
            });

            polylinesRef.current.push(polyline);
        });
    }, [directionsResults, selectedRouteIndex]);

    // Calculate selected route distance and fare
    const selectedRoute = directionsResults?.routes[selectedRouteIndex];
    const distanceKm = selectedRoute
        ? selectedRoute.legs.reduce((acc, leg) => acc + leg.distance.value, 0) / 1000
        : 0;
    const fare = (distanceKm * ratePerKm).toFixed(2);

    setTotalDistance(distanceKm);
    // setTotalFare(fare);

    return (
        <div className="max-w-6xl mx-auto mt-10">
  <div className="grid md:grid-cols-2 gap-8">
    {/* Left Panel - Map */}
    <div className="bg-white p-5 rounded-2xl shadow-md border border-gray-100 transition hover:shadow-lg">
      <h2 className="font-semibold text-lg text-gray-800 mb-3 flex items-center gap-2">
        🗺️ Adjust Your Route
      </h2>
      <div className="h-80 rounded-lg overflow-hidden relative border border-gray-200">
        <div ref={mapRef} style={{ height: "100%", width: "100%" }} />
      </div>
      <p className="text-gray-500 text-sm mt-3 flex items-center gap-1">
        <span className="text-blue-500 text-lg">📍</span> Drag the markers to adjust pickup and drop-off locations
      </p>
    </div>

    {/* Right Panel - Route Options */}
    <div className="space-y-6">
      {directionsResults && (
        <div className="bg-white p-5 rounded-2xl shadow-md border border-gray-100 transition hover:shadow-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            🚗 Select a Route
          </h3>
          <div className="space-y-3">
            {directionsResults.routes.map((route, index) => {
              const distance =
                route.legs.reduce((total, leg) => total + leg.distance.value, 0) / 1000;
              const duration =
                route.legs.reduce((total, leg) => total + leg.duration.value, 0);
              const durationMinutes = Math.round(duration / 60);

              const isSelected = selectedRouteIndex === index;

              return (
                <div
                  key={index}
                  onClick={() => setSelectedRouteIndex(index)}
                  className={`p-3 rounded-xl border cursor-pointer flex items-center justify-between transition-all duration-200 ${
                    isSelected
                      ? "border-blue-600 bg-gradient-to-r from-blue-50 to-blue-100 shadow-sm"
                      : "border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 flex items-center justify-center rounded-full text-white ${
                        isSelected ? "bg-blue-600" : "bg-gray-400"
                      }`}
                    >
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">
                        Route {index + 1}
                      </p>
                      <p className="text-sm text-gray-500">
                        {distance.toFixed(2)} km • {durationMinutes} mins
                      </p>
                    </div>
                  </div>
                  {isSelected && (
                    <span className="text-blue-600 font-semibold text-sm">
                      Selected ✓
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  </div>
</div>

    );
}
